/**
 * @file    useDragReorder.ts
 * @scope   SHARED
 * @summary Native HTML5 drag-and-drop reorder handlers for id-based lists.
 * @consumes Nothing domain-specific.
 * @produces useDragReorder, DRAG_ID_KEY
 * @sideEffects Writes/reads the dragged id through `DataTransfer`.
 * @notes The caller owns the list; this hook only resolves `from`/`to` and calls `onMove`.
 */
import React, { useCallback } from 'react';

/** DataTransfer key carrying the dragged item id. */
export const DRAG_ID_KEY = 'sourceId';

interface UseDragReorderParams {
  /** Resolves the current index of an item id (-1 when missing). */
  getIndexById: (id: string) => number;
  /** Called with the resolved indexes when a drop changes the position. */
  onMove: (from: number, to: number) => void;
}

export const useDragReorder = ({ getIndexById, onMove }: UseDragReorderParams) => {
  /** Stores the dragged item id. */
  const handleDragStart = useCallback((e: React.DragEvent<HTMLElement>, id: string) => {
    e.dataTransfer.setData(DRAG_ID_KEY, id);
  }, []);

  /** Allows dropping on the target. */
  const handleDragOver = useCallback((e: React.DragEvent<HTMLElement>) => e.preventDefault(), []);

  /** Resolves the dragged item and moves it to `targetIndex`. */
  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLElement>, targetIndex: number) => {
      e.preventDefault();
      const from = getIndexById(e.dataTransfer.getData(DRAG_ID_KEY));
      if (from < 0 || from === targetIndex) return;
      onMove(from, targetIndex);
    },
    [getIndexById, onMove]
  );

  return { handleDragStart, handleDragOver, handleDrop };
};
